import React, { useEffect, useMemo, useState } from 'react';
import { referralAPI, ReferralStats } from '../../services/api';
import './ShareLomi.css';

/**
 * "Invite a friend" affordance for the student Dashboard.
 *
 * Default render is a compact pill chip; tapping opens the modal with
 * the personalized invite link, native Share, WhatsApp, and Telegram
 * quick links. Pass `renderTrigger` to host the open-modal action in a
 * caller-controlled control (e.g. one segment of the partitioned status
 * strip on the dashboard).
 */
interface ShareLomiProps {
  renderTrigger?: (openModal: () => void, chipLabel: string) => React.ReactNode;
}

const ShareLomi: React.FC<ShareLomiProps> = ({ renderTrigger }) => {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await referralAPI.getStats();
        if (!cancelled) setStats(s);
      } catch {
        // Silent — chip still works as plain "Invite friends".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const inviteUrl = useMemo(() => {
    const code = stats?.referral_code;
    if (!code) return null;
    const base =
      typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.host}`
        : '';
    return `${base}/?ref=${code}`;
  }, [stats?.referral_code]);

  const inviteText = useMemo(() => {
    if (!inviteUrl) return '';
    return (
      "I've been using Lomi to study for the ESSLCE — past exams, AI tutor, " +
      `and lessons all in one place. Sign up free with my link: ${inviteUrl}`
    );
  }, [inviteUrl]);

  const count = stats?.count ?? 0;
  const chipLabel =
    count > 0
      ? `${count} ${count === 1 ? 'friend joined' : 'friends joined'}`
      : 'Invite friends';

  const copy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard might be blocked; the input below is selectable.
    }
  };

  const nativeShare = async () => {
    if (!inviteUrl) return;
    if (typeof navigator === 'undefined' || !navigator.share) {
      copy();
      return;
    }
    try {
      await navigator.share({
        title: 'Lomi — ESSLCE prep',
        text: inviteText,
        url: inviteUrl,
      });
    } catch {
      // User cancelled — no-op.
    }
  };

  const waUrl = inviteUrl
    ? `https://wa.me/?text=${encodeURIComponent(inviteText)}`
    : '#';
  const tgUrl = inviteUrl
    ? `https://t.me/share/url?url=${encodeURIComponent(
        inviteUrl
      )}&text=${encodeURIComponent(
        "I've been using Lomi to study for the ESSLCE — try it free:"
      )}`
    : '#';

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setOpen(true), chipLabel)
      ) : (
        <button
          type="button"
          className="invite-mini"
          onClick={() => setOpen(true)}
          aria-label="Invite a friend to Lomi"
        >
          <span className="invite-mini-icon" aria-hidden>🎁</span>
          <span className="invite-mini-label">{chipLabel}</span>
        </button>
      )}

      {open && (
        <div
          className="share-lomi-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Invite a friend"
          onClick={() => setOpen(false)}
        >
          <div
            className="share-lomi-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="share-lomi-modal__close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>

            <h3 className="share-lomi-modal__title">Invite a friend</h3>
            <p className="share-lomi-modal__sub">
              {count > 0 ? (
                <>
                  <strong>
                    {count} {count === 1 ? 'friend' : 'friends'} joined.
                  </strong>{' '}
                  Share your link with more.
                </>
              ) : (
                <>Share your link with a classmate.</>
              )}
            </p>

            {inviteUrl ? (
              <>
                <div className="share-lomi-modal__url">
                  <input
                    className="share-lomi-modal__url-input"
                    value={inviteUrl}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label="Your invite URL"
                  />
                  <button
                    type="button"
                    className="share-lomi-modal__copy"
                    onClick={copy}
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>

                <div className="share-lomi-modal__icons">
                  <a
                    className="share-ico"
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on WhatsApp"
                  >
                    <span className="share-ico__badge">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#25D366" d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.494 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z" />
                        <path fill="#FFF" d="M17.507 14.307c-.3-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51l-.571-.015c-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345z" />
                      </svg>
                    </span>
                    <span className="share-ico__label">WhatsApp</span>
                  </a>
                  <a
                    className="share-ico"
                    href={tgUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Telegram"
                  >
                    <span className="share-ico__badge">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#229ED9" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Z" />
                        <path fill="#FFF" d="M5.491 11.74l11.57-4.461c.537-.194 1.006.131.832.943l-1.97 9.281c-.146.658-.537.818-1.084.508l-3-2.211-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.022c.242-.213-.054-.333-.373-.121l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953Z" />
                      </svg>
                    </span>
                    <span className="share-ico__label">Telegram</span>
                  </a>
                  {typeof navigator !== 'undefined' && !!(navigator as Navigator).share && (
                    <button
                      type="button"
                      className="share-ico share-ico--more"
                      onClick={nativeShare}
                      aria-label="More share options"
                    >
                      <span className="share-ico__badge">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </span>
                      <span className="share-ico__label">More</span>
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="share-lomi-modal__empty">
                Loading your invite link…
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ShareLomi;
